import {
  Chip,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Edit05, Trash01 } from '../../../../assets';
import { DemandModifier } from '../types/DemandModifierTypes';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';

interface DemandModifiersTableProps {
  modifiers: DemandModifier[];
  onEdit: (modifier: DemandModifier) => void;
  onDelete: (modifier: DemandModifier) => void;
  onCreate?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canCreate?: boolean;
}

const DemandModifiersTable = ({
  modifiers,
  onEdit,
  onDelete,
  onCreate,
  canEdit = true,
  canDelete = true,
  canCreate = true,
}: DemandModifiersTableProps) => {
  const { t } = useTranslation('demandModifiers');
  const theme = useTheme();
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof DemandModifier>('name');

  const handleSort = (property: keyof DemandModifier) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedModifiers = useMemo(() => {
    return [...modifiers].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }
      
      return order === 'asc' ? comparison : -comparison;
    });
  }, [modifiers, order, orderBy]);

  const getStatusLabel = (modifier: DemandModifier) => {
    const today = dayjs();
    const startDate = dayjs(modifier.startDate);
    const endDate = dayjs(modifier.endDate);

    if (today.isBefore(startDate)) {
      return { label: t('status.scheduled'), color: 'info' as const };
    } else if (today.isAfter(endDate)) {
      return { label: t('status.expired'), color: 'default' as const };
    } else if (modifier.isActive) {
      return { label: t('status.active'), color: 'success' as const };
    } else {
      return { label: t('status.inactive'), color: 'default' as const };
    }
  };

  const getTargetLabel = (modifier: DemandModifier) => {
    const itemLabel = (() => {
      switch (modifier.targetType) {
        case 'allItems':
          return t('target.allItems');
        case 'specificCategories':
          return t('target.categories', { count: modifier.targetCategories.length });
        case 'specificSKUs':
          return t('target.skus', { count: modifier.targetSKUs.length });
        default:
          return '-';
      }
    })();

    const locationLabel = (() => {
      switch (modifier.locationType) {
        case 'allFranchises':
          return t('target.allFranchises');
        case 'specificFranchises':
          return t('target.franchises', { count: modifier.targetFranchises.length });
        case 'specificStores':
          return t('target.stores', { count: modifier.targetStores.length });
        default:
          return '-';
      }
    })();

    return `${itemLabel} • ${locationLabel}`;
  };

  const getTypeLabel = (modifier: DemandModifier) => {
    if (modifier.modifierType === 'percentageUplift') {
      return t('type.uplift');
    } else {
      return t('type.quantityOffset');
    }
  };

  const getValueLabel = (modifier: DemandModifier) => {
    if (modifier.modifierType === 'percentageUplift') {
      if (modifier.upliftPercentage > 0) {
        return `+${modifier.upliftPercentage}%`;
      }

      if (modifier.upliftPercentage < 0) {
        return `${modifier.upliftPercentage}%`;
      }

      return '0%';
    } else {
      const quantityOffset = modifier.quantityOffset;

      if (quantityOffset == null) {
        return '-';
      }

      if (quantityOffset > 0) {
        return `+${quantityOffset}`;
      }

      return quantityOffset.toString();
    }
  };

  if (modifiers.length === 0) {
    return (
      <EmptyTable
        text={t('title')}
        onClear={() => {}}
        onAdd={onCreate ?? (() => {})}
        addItemText={t('createButton')}
        onAddIsCreating={false}
        showOnAdd={canCreate}
        headerHeight={55}
        bodyRowHeight={45}
        withPaginationShown={false}
        showClear={false}
        showSecondaryText={false}
      />
    );
  }

  return (
    <>
      <TableHeader>
        {t('title')}
        {modifiers.length > 0 && <Pill text={`${modifiers.length} ${t('title')}`}></Pill>}
      </TableHeader>
      <TableContainer sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
        <Table aria-label="demand-modifiers-table">
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableCell sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}>
                  {t('table.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                {t('table.status')}
              </TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableSortLabel
                  active={orderBy === 'startDate'}
                  direction={orderBy === 'startDate' ? order : 'asc'}
                  onClick={() => handleSort('startDate')}>
                  {t('table.startDate')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableSortLabel
                  active={orderBy === 'endDate'}
                  direction={orderBy === 'endDate' ? order : 'asc'}
                  onClick={() => handleSort('endDate')}>
                  {t('table.endDate')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                {t('table.target')}
              </TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableSortLabel
                  active={orderBy === 'modifierType'}
                  direction={orderBy === 'modifierType' ? order : 'asc'}
                  onClick={() => handleSort('modifierType')}>
                  {t('table.type')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                <TableSortLabel
                  active={orderBy === 'upliftPercentage'}
                  direction={orderBy === 'upliftPercentage' ? order : 'asc'}
                  onClick={() => handleSort('upliftPercentage')}>
                  {t('table.value')}
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedModifiers.map((modifier) => {
              const status = getStatusLabel(modifier);
              return (
                <TableRow
                  key={modifier.id}
                  hover>
                  <TableCell>{modifier.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>{dayjs(modifier.startDate).format('MM/DD/YYYY')}</TableCell>
                  <TableCell>{dayjs(modifier.endDate).format('MM/DD/YYYY')}</TableCell>
                  <TableCell>{getTargetLabel(modifier)}</TableCell>
                  <TableCell>{getTypeLabel(modifier)}</TableCell>
                  <TableCell>{getValueLabel(modifier)}</TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <Tooltip title={t('table.editTooltip')}>
                        <IconButton
                          sx={{ mr: '10px' }}
                          size="small"
                          aria-label={t('table.editTooltip')}
                          onClick={() => onEdit(modifier)}
                        >
                          <SvgIcon color="primary">
                            <Edit05 />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title={t('table.deleteTooltip')}>
                        <IconButton
                          size="small"
                          aria-label={t('table.deleteTooltip')}
                          onClick={() => onDelete(modifier)}>
                          <SvgIcon color="error">
                            <Trash01 />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default DemandModifiersTable;
